/**CORE LIBRARIES IMPORT */
import React from "react";
import { Link } from "react-router-dom";

/**DATA / HOOKS IMPORT */
import { CRO_NUMBER, CRO_PLACEHOLDER, PRIVACY_PATH, REFUND_POLICY_PATH, TERMS_PATH } from "../data/legal";
import usePageTitle from "../hooks/usePageTitle";

const EMAIL_RE = /[\w.+-]+@[\w-]+\.[\w.-]*\w/;
const TOKEN_RE = /(\*\*[^*]+\*\*|[\w.+-]+@[\w-]+\.[\w.-]*\w|https:\/\/[^\s)]*[^\s).,;])/g; // bold | email | URL

/** Inline: **bold**, email addresses and https links (text itself unchanged). */
function inline(text, key) {
  return text.split(TOKEN_RE).filter(Boolean).map((p, i) => {
    const k = `${key}-${i}`;
    if (p.startsWith("**") && p.endsWith("**")) return <strong key={k}>{inline(p.slice(2, -2), k)}</strong>;
    if (new RegExp(`^${EMAIL_RE.source}$`).test(p)) return <a key={k} href={`mailto:${p}`}>{p}</a>;
    if (p.startsWith("https://thegreywolf.ie")) return <Link key={k} to={p.slice("https://thegreywolf.ie".length) || "/"}>{p}</Link>;
    if (p.startsWith("https://")) return <a key={k} href={p} target="_blank" rel="noopener noreferrer">{p}</a>;
    return <React.Fragment key={k}>{p}</React.Fragment>;
  });
}

const withCro = (text) => text.replaceAll("{{CRO}}", CRO_NUMBER || CRO_PLACEHOLDER);

/** Renders the approved Markdown: "# " title, "## " headings, "- " lists, paragraphs (line breaks kept). */
export function LegalDocument({ markdown }) {
  const blocks = withCro(markdown).split(/\n\s*\n/).map((b) => b.trim()).filter(Boolean);
  return blocks.map((b, i) => {
    if (b.startsWith("# ")) return <h1 key={i} className="page-title legal-title">{b.slice(2)}</h1>;
    if (b.startsWith("## ")) return <h2 key={i} className="legal-heading">{b.slice(3)}</h2>;
    const lines = b.split("\n");
    if (lines.every((l) => l.startsWith("- "))) {
      return <ul key={i} className="section-text legal-list">{lines.map((l, j) => <li key={j}>{inline(l.slice(2), `${i}-${j}`)}</li>)}</ul>;
    }
    if (!CRO_NUMBER && b.includes(CRO_PLACEHOLDER)) {
      const [before, after] = b.split(CRO_PLACEHOLDER);
      return <p key={i} className="section-text">{before}<mark className="legal-placeholder">{CRO_PLACEHOLDER}</mark>{after}</p>;
    }
    return (
      <p key={i} className="section-text">
        {lines.map((l, j) => <React.Fragment key={j}>{j > 0 && <br />}{inline(l, `${i}-${j}`)}</React.Fragment>)}
      </p>
    );
  });
}

const LegalPage = ({ title, markdown }) => {
  usePageTitle(title);
  return (
    <main className="simple-page legal-page">
      <article>
        <LegalDocument markdown={markdown} />
      </article>
      <nav className="legal-links" aria-label="Policies">
        <Link to={TERMS_PATH}>Terms &amp; Conditions</Link>
        <span aria-hidden="true"> · </span>
        <Link to={REFUND_POLICY_PATH}>Refund &amp; Cancellation Policy</Link>
        <span aria-hidden="true"> · </span>
        <Link to={PRIVACY_PATH}>Privacy Policy</Link>
      </nav>
    </main>
  );
};

export default LegalPage;
